import { FormBuilder } from '@/components/formBuilder';
import { KButton, KText } from '@/ui-library/components';
import { Box, Flex } from '@chakra-ui/react';
import { useRef, useMemo, useState } from 'react';
import { generateOfferPayload } from '@/components/ats/OfferInitiation/offerFormHelpers';
import { offerDetailsAPI } from '../../api';
import { useShowToast } from '@/hooks';

interface EmptyTokensFormProps {
  nonFilledValidTokens: string[];
  detailsFormConfig: any[];
  currentOffer: any;
  applicationId: string | number;
  existingFormValues?: Record<string, any>;
  onCancel: () => void;
  onSaveSuccess: () => void;
}

export const EmptyTokensForm = ({
  nonFilledValidTokens,
  detailsFormConfig,
  currentOffer,
  applicationId,
  existingFormValues = {},
  onCancel,
  onSaveSuccess,
}: EmptyTokensFormProps) => {
  const formRef = useRef(null);
  const toast = useShowToast();
  const [isSaving, setIsSaving] = useState(false);

  const missingFieldsConfig = useMemo(() => {
    const tokenSet = new Set(nonFilledValidTokens);
    return detailsFormConfig
      .flatMap((section) => section?.fields ?? [section])
      .filter((field) => field?.tokenName && tokenSet.has(field.tokenName));
  }, [nonFilledValidTokens, detailsFormConfig]);

  const initialValues = useMemo(() => {
    const missingValues = missingFieldsConfig.reduce(
      (acc, field) => {
        acc[field.name] = currentOffer?.[field.name] ?? '';
        return acc;
      },
      {} as Record<string, any>
    );
    // Spread existingFormValues first so conditional-dependency fields (e.g.
    // award_type) are present; missing field values take precedence.
    return { ...existingFormValues, ...missingValues };
  }, [missingFieldsConfig, currentOffer, existingFormValues]);

  const handleSave = async () => {
    const newValues = formRef.current?.values;
    if (!newValues) return;

    // Merge existing offer form values with the newly filled token fields so
    // the update doesn't wipe fields that were already saved on the offer.
    // Fall back to the offer's linked requisition code if it's not in existingFormValues
    // (post-approval send flow where req feature is on may require it in the payload).
    const requisitionFallback = !existingFormValues?.requisitions_codes
      ? { requisitions_codes: currentOffer?.requisition?.code }
      : {};
    const values = {
      ...requisitionFallback,
      ...existingFormValues,
      ...newValues,
    };

    setIsSaving(true);
    try {
      const offerPayload = generateOfferPayload({ values, applicationId });
      const response = (await offerDetailsAPI.updateOffer({
        payload: offerPayload,
        id: currentOffer?.id,
      })) as any;

      if (response?.errors?.length > 0) {
        toast({
          title: 'Failed to save offer fields',
          status: 'error',
        });
        return;
      }

      onSaveSuccess();
    } catch {
      toast({ title: 'Failed to save offer fields', status: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box p="24px" mt="12px">
      <KText size="md-semi-bold" color="secondary-900" mb="4px">
        Empty tokens detected in the offer letter template
      </KText>
      <KText size="sm-regular" color="secondary-600" mb="16px">
        Fill the missing information to generate the offer letter
      </KText>

      <FormBuilder
        initialValues={initialValues}
        config={missingFieldsConfig}
        formRef={formRef}
      />

      <Flex justifyContent="flex-end" gap="8px" mt="16px">
        <KButton variant="secondary" onClick={onCancel} isDisabled={isSaving}>
          Cancel
        </KButton>
        <KButton variant="primary" onClick={handleSave} isLoading={isSaving}>
          Save & generate offer
        </KButton>
      </Flex>
    </Box>
  );
};
